/**
 * 前端安全工具库
 * 提供：设备指纹生成、RSA公钥加密、AES加解密
 * 依赖：crypto-js（通过 window.CryptoJS 或 import）
 */

// ==================== 设备指纹生成 ====================

/**
 * 生成 Canvas 指纹
 * 利用浏览器渲染差异产生唯一字符串
 */
function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 220;
    canvas.height = 30;
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f00';
    ctx.fillRect(0, 0, 100, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('BUS-Security-FP', 2, 2);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('BUS-Security-FP', 4, 4);
    return canvas.toDataURL();
  } catch (e) {
    return 'canvas_not_supported';
  }
}

/**
 * SHA256 简易实现（使用 Web Crypto API）
 * @param {string} message
 * @returns {Promise<string>} 十六进制字符串
 */
async function sha256Async(message) {
  try {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    // 降级：返回简单哈希
    let hash = 0;
    for (let i = 0; i < message.length; i++) {
      const char = message.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16).padStart(8, '0');
  }
}

/**
 * 生成设备指纹
 * 多维特征：UA + 屏幕 + 时区 + 语言 + 平台 + CPU核数 + Canvas指纹
 * @returns {Promise<string>} 设备指纹（64位十六进制字符串）
 */
async function generateDeviceFingerprint() {
  const features = [
    navigator.userAgent || '',
    navigator.language || '',
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    String(new Date().getTimezoneOffset()),
    navigator.platform || '',
    String(navigator.hardwareConcurrency || 0),
    getCanvasFingerprint()
  ];
  const raw = features.join('|');
  return await sha256Async(raw);
}

// 缓存设备指纹（页面生命周期内不变）
let _cachedFingerprint = null;

/**
 * 获取设备指纹（带缓存）
 * @returns {Promise<string>}
 */
export async function getDeviceFingerprint() {
  if (_cachedFingerprint) return _cachedFingerprint;
  _cachedFingerprint = await generateDeviceFingerprint();
  return _cachedFingerprint;
}

// ==================== RSA 公钥加密 ====================

/**
 * 将 Base64 公钥字符串转换为 CryptoKey
 * @param {string} base64PublicKey
 * @returns {Promise<CryptoKey>}
 */
async function importRsaPublicKey(base64PublicKey) {
  const binaryDer = Uint8Array.from(atob(base64PublicKey), c => c.charCodeAt(0));
  return await crypto.subtle.importKey(
    'spki',
    binaryDer.buffer,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt']
  );
}

/**
 * 用 RSA 公钥加密数据（返回 Base64 字符串）
 * @param {string} base64PublicKey RSA公钥（Base64）
 * @param {Uint8Array} data 要加密的字节数组
 * @returns {Promise<string>} Base64编码的密文
 */
async function rsaEncrypt(base64PublicKey, data) {
  const publicKey = await importRsaPublicKey(base64PublicKey);
  const encrypted = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    data
  );
  return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
}

// ==================== AES-128-CBC 加解密 ====================

/**
 * AES-128-CBC 解密（密文前16字节为IV，后续为密文，整体Base64编码）
 * @param {string} encryptedBase64 Base64编码的密文（含IV前缀）
 * @param {Uint8Array} keyBytes 16字节AES密钥
 * @returns {Promise<string>} 解密后的明文字符串
 */
export async function aesDecrypt(encryptedBase64, keyBytes) {
  const raw = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
  const iv = raw.slice(0, 16);
  const cipherText = raw.slice(16);
  const cryptoKey = await crypto.subtle.importKey(
    'raw', keyBytes, { name: 'AES-CBC' }, false, ['decrypt']
  );
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv }, cryptoKey, cipherText
  );
  return new TextDecoder().decode(decrypted);
}

/**
 * AES-128-CBC 加密（结果前16字节为IV，后续为密文，整体Base64编码）
 * @param {string} plainText 明文
 * @param {Uint8Array} keyBytes 16字节AES密钥
 * @returns {Promise<string>} Base64编码的密文（含IV前缀）
 */
export async function aesEncryptStr(plainText, keyBytes) {
  return aesEncrypt(plainText, keyBytes);
}

async function aesEncrypt(plainText, keyBytes) {
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const cryptoKey = await crypto.subtle.importKey(
    'raw', keyBytes, { name: 'AES-CBC' }, false, ['encrypt']
  );
  const encoded = new TextEncoder().encode(plainText);
  const cipherBuffer = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv }, cryptoKey, encoded
  );
  const result = new Uint8Array(16 + cipherBuffer.byteLength);
  result.set(iv, 0);
  result.set(new Uint8Array(cipherBuffer), 16);
  return btoa(String.fromCharCode(...result));
}

// ==================== 登录请求加密主函数 ====================

/**
 * 加密登录请求体
 * 流程：生成随机AES密钥 → RSA公钥加密AES密钥 → AES加密请求体（含时间戳）
 *
 * @param {string} publicKey RSA公钥（Base64）
 * @param {Object} loginData 登录数据 {phone, password, deviceFingerprint}
 * @returns {Promise<{encryptedAesKey: string, encryptedBody: string}>}
 */
export async function encryptLoginRequest(publicKey, loginData) {
  // 生成随机16字节AES密钥
  const aesKeyBytes = crypto.getRandomValues(new Uint8Array(16));

  // RSA加密AES密钥
  const encryptedAesKey = await rsaEncrypt(publicKey, aesKeyBytes);

  // 构建请求体（含时间戳防重放）
  const body = JSON.stringify({
    ...loginData,
    timestamp: Date.now()
  });

  // AES加密请求体
  const encryptedBody = await aesEncrypt(body, aesKeyBytes);

  return { encryptedAesKey, encryptedBody, aesKeyBytes };
}
